import React, { useRef, useState, useEffect } from 'react';
import YouTube from 'react-youtube';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import {
    VideoProductCard,
    VideoInfo,
    VideoContainer,
    ScrollableContainer,
    ScrollableItem,
    LeftArrow,
    RightArrow
} from './styled';

// Inline InstagramEmbed component similar to what you had before
const InstagramEmbed = ({ url }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const loadInstagram = async () => {
            if (!window.instgrm) {
                const scriptElement = document.createElement('script');
                scriptElement.src = 'https://www.instagram.com/embed.js';
                scriptElement.async = true;
                document.body.appendChild(scriptElement);
            }

            const processEmbeds = () => {
                if (window.instgrm && window.instgrm.Embeds) {
                    window.instgrm.Embeds.process();
                }
            };

            if (window.instgrm) {
                processEmbeds();
            } else {
                const waitForLoad = setInterval(() => {
                    if (window.instgrm) {
                        processEmbeds();
                        clearInterval(waitForLoad);
                    }
                }, 100);
                setTimeout(() => clearInterval(waitForLoad), 10000);
            }
        };

        loadInstagram();
    }, [url]);

    let finalUrl = url;
    // Normalize /reels/ to /reel/ if present
    if (finalUrl.includes('/reels/')) {
        finalUrl = finalUrl.replace('/reels/', '/reel/');
    }

    const embedCode = 
        `<blockquote 
          class="instagram-media" 
          data-instgrm-captioned 
          data-instgrm-permalink="${finalUrl}?utm_source=ig_embed&utm_campaign=loading"
          data-instgrm-version="14"
          style="background:#FFF; border:0; border-radius:17px; 
                 box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); 
                 margin: 1px; max-width:540px; min-width:315px; padding:0; width:99.375%;">
          <div style="padding:16px;">
            <a href="${finalUrl}?utm_source=ig_embed&utm_campaign=loading" 
               style="background:#FFFFFF; line-height:0; padding:0 0; 
                      text-align:center; text-decoration:none; width:100%;" 
               target="_blank"></a>
          </div>
        </blockquote>`;

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                position: 'relative'
            }}
            dangerouslySetInnerHTML={{ __html: embedCode }}
        />
    );
};

// New function to extract YouTube video data (handles Shorts and normal URLs)
const extractYouTubeVideoData = (url) => {
    try {
        let videoId = null;
        let isShort = false;

        const shortMatch = url.match(/(?:youtu\.be\/shorts\/|youtube\.com\/shorts\/)([^?/]+)/);
        const normalYoutuMatch = url.match(/youtu\.be\/([^?]+)/);
        const normalYoutubeMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);

        if (shortMatch && shortMatch[1]) {
            isShort = true;
            videoId = shortMatch[1];
        } else if (normalYoutuMatch && normalYoutuMatch[1]) {
            videoId = normalYoutuMatch[1];
        } else if (normalYoutubeMatch && normalYoutubeMatch[1]) {
            videoId = normalYoutubeMatch[1];
        }

        return { videoId, isShort };
    } catch {
        return { videoId: null, isShort: false };
    }
};

const VideoCard = ({ item, theme }) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const isSingleVideo = item.videos.length === 1;
    const containerRef = useRef(null);

    useEffect(() => {
        const updateArrowPositions = () => {
            if (containerRef.current) {
                setScrollPosition(containerRef.current.scrollLeft);
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', updateArrowPositions);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', updateArrowPositions);
            }
        };
    }, []);

    const scroll = (direction) => {
        if (containerRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300;
            containerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    if (!item.videos || item.videos.length === 0) {
        return <div>No video available</div>;
    }

    const renderVideo = (video, index) => {
        // Check the type and handle accordingly
        const isReel = video.type === 'instagram_reel';

        // Use the new extract function for YouTube videos
        const { videoId, isShort } = extractYouTubeVideoData(video.videoUrl);

        // Determine padding based on type
        // - Reels: 185%
        // - Shorts: 125%
        // - Standard YouTube: 56.25%
        let paddingTop = '56.25%';
        if (isReel) {
            paddingTop = '185%';
        } else if (isShort) {
            paddingTop = '177%';
        }

        return (
            <ScrollableItem key={`video-${index}`} isSingleVideo={isSingleVideo}>
                <VideoProductCard theme={theme}>
                    <div style={{ 
                        width: '100%', 
                        position: 'relative', 
                        paddingTop: paddingTop,
                        overflow: 'hidden'
                    }}>
                        {!isReel && videoId && (
                            <YouTube
                                videoId={videoId}
                                opts={{
                                    width: '100%',
                                    height: '100%',
                                    playerVars: { autoplay: 0 },
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%'
                                }}
                            />
                        )}
                        {isReel && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                paddingTop: '177%'
                            }}>
                                <InstagramEmbed url={video.videoUrl} />
                            </div>
                        )}
                    </div>
                    {(video.title || video.subtitle) && !isReel && !isShort && (
                        <VideoInfo theme={theme}>
                            {video.title && <h3>{video.title}</h3>}
                            {video.subtitle && <p>{video.subtitle}</p>}
                        </VideoInfo>
                    )}
                </VideoProductCard>
            </ScrollableItem>
        );
    };

    return (
        <VideoContainer>
            <ScrollableContainer ref={containerRef} isSingleVideo={isSingleVideo}>
                {item.videos.map((video, index) => renderVideo(video, index))}
            </ScrollableContainer>
            {item.videos.length > 1 && scrollPosition > 0 && (
                <LeftArrow onClick={() => scroll('left')}>
                    <LeftOutlined />
                </LeftArrow>
            )}
            {item.videos.length > 1 && containerRef.current && 
             (scrollPosition < (containerRef.current.scrollWidth - containerRef.current.clientWidth)) && (
                <RightArrow onClick={() => scroll('right')}>
                    <RightOutlined />
                </RightArrow>
            )}
        </VideoContainer>
    );
};

export default VideoCard;
